import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IconSelector, IconSearch } from '@tabler/icons-react';
import clsx from 'clsx';
import useService from '../../../../injector/hooks/useService';
import { UserService } from '../../../../core/services/user.service';
import { WorkspacesAPI } from '../../../../containers/team/Workspace/api/WorkspacesAPI';
import { getErrorMessageFromObj } from '../../../../core/utils/request';
import { toasterService, Tooltip } from '@hevoio/hevo-ui-toolkit';
import styles from './WorkspaceSwitcher.module.scss';
import { WORKSPACE_URL_SVG } from '../../../../shared/constants/brand-logo';
import { IWorkspaceAccess } from '../../../../containers/team/Workspace/interfaces/workspace';

interface WorkspaceSwitcherProps {
  expanded?: boolean;
}

export function WorkspaceSwitcher({ expanded = false }: WorkspaceSwitcherProps) {
  const userService = useService(UserService);

  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [context, setContext] = useState(() => userService.getWorkspaceContext());
  const [searchQuery, setSearchQuery] = useState('');
  const [flyoutTop, setFlyoutTop] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const collapsedTriggerRef = useRef<HTMLButtonElement>(null);

  const { currentTeam, workspaceAccess } = context;
  const options: IWorkspaceAccess[] = workspaceAccess ?? [];

  const filteredOptions = searchQuery
    ? options.filter(opt => opt.team_name.toLowerCase().includes(searchQuery.toLowerCase()))
    : options;

  useEffect(() => {
    const sub = userService.onUserUpdate$.subscribe(() => {
      setContext(userService.getWorkspaceContext());
    });
    return () => sub.unsubscribe();
  }, [userService]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Keep the switcher in sync when another tab switches workspace and writes
  // updated user data to localStorage.
  useEffect(() => {
    const handleStorageChange = () => {
      setContext(userService.getWorkspaceContext());
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [userService]);

  if (!currentTeam || options.length <= 1) return null;

  const isDisabled = switching;

  const handleSelect = async (option: IWorkspaceAccess) => {
    if (option.team_id === currentTeam?.team_id || switching) return;
    setOpen(false);
    setSearchQuery('');
    setSwitching(true);
    try {
      await WorkspacesAPI.selectTeam(option.team_id);
      window.location.href = '/';
    } catch (err) {
      toasterService.pop(
        'error',
        undefined,
        getErrorMessageFromObj(err) || 'Failed to switch workspace'
      );
      setSwitching(false);
    }
  };

  const toggleOpen = () => {
    if (isDisabled) return;
    if (!open && !expanded && collapsedTriggerRef.current) {
      const rect = collapsedTriggerRef.current.getBoundingClientRect();
      setFlyoutTop(rect.top);
    }
    if (open) setSearchQuery('');
    collapsedTriggerRef.current?.blur();
    setOpen(o => !o);
  };

  const searchInput = (
    <div className={styles.searchWrapper}>
      <IconSearch size={20} strokeWidth={1.5} className={styles.searchIcon} />
      <input
        className={styles.searchInput}
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        placeholder='Search...'
        autoFocus
      />
    </div>
  );

  const workspaceOptionsList = filteredOptions.map(option => {
    const isActive = option.team_id === currentTeam.team_id;
    return (
      <button
        key={option.team_id}
        className={clsx(styles.option, isActive && styles.optionActive)}
        onClick={() => handleSelect(option)}
        data-id={`workspace-switcher-option-${option.team_id}`}
      >
        <span className={styles.optionName}>{option.team_name}</span>
      </button>
    );
  });

  return (
    <div
      className={clsx(styles.container, expanded && styles.containerExpanded)}
      ref={containerRef}
    >
      <div className={styles.triggerClip}>
        <Tooltip
          dataId='workspace-switcher-tooltip'
          title={`Workspace - ${currentTeam.team_name}`}
          placement='right'
          disableFocusListener
          forceClose={expanded || open}
        >
          <button
            ref={collapsedTriggerRef}
            className={clsx(styles.trigger, open && styles.triggerOpen)}
            onClick={toggleOpen}
            disabled={isDisabled}
            data-id='workspace-switcher-trigger'
          >
            <span className={styles.iconWrap}>
              <img src={WORKSPACE_URL_SVG} alt='Workspace' width={20} height={20} />
            </span>
            <span className={clsx(styles.teamName, expanded && styles.teamNameVisible)}>
              {currentTeam.team_name}
            </span>
            {!isDisabled && (
              <IconSelector
                size={16}
                strokeWidth={1.75}
                className={clsx(styles.chevron, expanded && styles.chevronVisible)}
              />
            )}
          </button>
        </Tooltip>
      </div>

      {open && (
        <div
          className={styles.backdrop}
          onClick={() => {
            setOpen(false);
            setSearchQuery('');
          }}
        />
      )}

      {expanded && open && (
        <div className={styles.expandedDropdown} data-id='workspace-switcher-dropdown'>
          {searchInput}
          {workspaceOptionsList}
        </div>
      )}
      {!expanded && open && (
        <div
          className={styles.flyout}
          style={{ top: flyoutTop }}
          data-id='workspace-switcher-flyout'
        >
          {searchInput}
          {workspaceOptionsList}
        </div>
      )}

      {switching &&
        ReactDOM.createPortal(
          <div className={styles.switchingOverlay} data-id='workspace-switching-overlay'>
            <div className={styles.switchingContent}>
              <div className={styles.switchingSpinner} />
              <span className={styles.switchingText}>Switching workspace…</span>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
